import React from 'react';

import { PatternFormat } from 'react-number-format';
import {
  FieldValues,
  Path,
  PathValue,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import { InputBaseProps, TextFieldProps, TextField } from '@mui/material';
import { COUNTRY_CODE_PREFIX, PHONE_VIEW_FORMAT } from '../../../constants/phoneFormat';
import { addValidateRules } from '../../../utils/reactHookFormUtils/addValidateRules/addValidateRules';
import { validatePhone } from '../../../utils/reactHookFormUtils/validatePhone/validatePhone';

export type PhoneNumberInputProps<T extends FieldValues, TName extends Path<T>> = {
  InputProps?: InputBaseProps;
  dataTestId?: string;
  allowEmptyFormatting?: boolean;
} & UseControllerProps<T, TName> &
  Pick<
    TextFieldProps,
    | 'label'
    | 'variant'
    | 'size'
    | 'helperText'
    | 'className'
    | 'placeholder'
    | 'disabled'
    | 'autoFocus'
    | 'margin'
  >;

/**
 * show format "+1 (123) 123 1231"
 * form value "+11231231231"
 */
const PhoneNumberInput = <T extends FieldValues, TName extends Path<T>>({
  control,
  name,
  rules,
  variant,
  defaultValue = '' as PathValue<T, TName>,
  shouldUnregister,
  helperText,
  InputProps,
  dataTestId,
  allowEmptyFormatting = true,
  ...props
}: PhoneNumberInputProps<T, TName>) => {
  const {
    field: { onChange, value, ref, ...field },
    fieldState: { error },
  } = useController({
    control,
    name,
    defaultValue,
    shouldUnregister,
    rules: addValidateRules(rules, { validatePhone }),
  });

  return (
    <PatternFormat
      {...field}
      allowEmptyFormatting={allowEmptyFormatting}
      customInput={TextField}
      onValueChange={(values) => {
        const formValue = values.value ? COUNTRY_CODE_PREFIX + values.value : '';
        onChange(formValue);
      }}
      variant={variant}
      value={value.replace(COUNTRY_CODE_PREFIX, '')}
      helperText={error?.message || helperText}
      error={!!error}
      required={!!rules?.required}
      fullWidth
      inputRef={ref}
      slotProps={{
        input: InputProps,
      }}
      type="tel"
      mask="_"
      format={PHONE_VIEW_FORMAT}
      data-test-id={dataTestId}
      {...props}
    />
  );
};

export { PhoneNumberInput };
